import React, { useMemo } from "react";
import { useFindMany } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  VStack,
  HStack,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Alert,
  AlertIcon,
  Heading,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { useInstitutionConfig } from "./InstitutionConfigContext";

export default function GeneralSettingsTab() {
  const {
    formData,
    institutionConfig,
    decodedInstitution,
    institution,
    updating,
    updateConfig,
    refetchInstitutionConfig,
    handleUpdateField,
    toast,
  } = useInstitutionConfig();

  const [{ data: institutionUsers, fetching: loadingInstitutionUsers }] = useFindMany(api.user, {
    filter: { institution: { equals: decodedInstitution } },
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      institutionRole: true,
    },
  });

  const [{ data: activeStudentMemberships, fetching: loadingStudentMemberships }] = useFindMany(api.cohortMembership, {
    filter: {
      active: { equals: true },
      primaryRole: { equals: "student" },
    },
    select: {
      id: true,
      user: { id: true },
    },
  });

  const liveUsedSeats = useMemo(() => {
    if (!Array.isArray(institutionUsers) || !Array.isArray(activeStudentMemberships)) {
      return institutionConfig?.usedSeats || 0;
    }

    const institutionUserIds = new Set(institutionUsers.map((user) => user.id));
    const enrolledStudentIds = new Set();

    activeStudentMemberships.forEach((membership) => {
      const membershipUserId = membership?.user?.id;
      if (membershipUserId && institutionUserIds.has(membershipUserId)) {
        enrolledStudentIds.add(membershipUserId);
      }
    });

    return enrolledStudentIds.size;
  }, [institutionUsers, activeStudentMemberships, institutionConfig?.usedSeats]);

  const seatsAreLoading = loadingInstitutionUsers || loadingStudentMemberships;
  const purchasedSeats = institutionConfig?.purchasedSeats || 0;
  const usedSeats = seatsAreLoading ? (institutionConfig?.usedSeats || 0) : liveUsedSeats;
  const availableSeats = purchasedSeats - usedSeats;
  const institutionAdmins = useMemo(() => {
    if (!Array.isArray(institutionUsers)) return [];

    return institutionUsers.filter((user) => {
      const roles = Array.isArray(user?.institutionRole)
        ? user.institutionRole
        : [user?.institutionRole];
      return roles
        .map((role) => String(role || "").trim().toLowerCase())
        .filter(Boolean)
        .includes("administrator");
    });
  }, [institutionUsers]);

  return (
    <VStack spacing={8} align="stretch">
      {/* Institution Details */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Institution Details</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl>
              <FormLabel color="white">Institution Name</FormLabel>
              <Input
                value={formData.institution || ""}
                isReadOnly
                bg="gray.700"
                borderColor="gray.600"
                color="gray.300"
              />
            </FormControl>
            <FormControl>
              <FormLabel color="white">Timezone</FormLabel>
              <Select
                value={formData.timezone || ""}
                onChange={(e) => handleUpdateField("timezone", e.target.value)}
                bg="gray.700"
                borderColor="gray.600"
                color="white"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </Select>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Institution Administrators</Heading>
          <Text fontSize="sm" color="gray.400" mt={2}>
            These users have the code-level `institutionRole=administrator` permission for {decodedInstitution}.
          </Text>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            {loadingInstitutionUsers ? (
              <Text color="gray.400">Loading institution administrators...</Text>
            ) : institutionAdmins.length > 0 ? (
              institutionAdmins.map((user) => {
                const fullName = [user?.firstName, user?.lastName]
                  .map((value) => String(value || "").trim())
                  .filter(Boolean)
                  .join(" ");
                const displayName = fullName || user?.email || `User ${user?.id}`;

                return (
                  <HStack
                    key={user.id}
                    justify="space-between"
                    p={3}
                    bg="gray.700"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.600"
                  >
                    <VStack align="start" spacing={0}>
                      <Text color="white" fontWeight="semibold">{displayName}</Text>
                      {user?.email && user.email !== displayName && (
                        <Text color="gray.400" fontSize="sm">{user.email}</Text>
                      )}
                    </VStack>
                    <Badge colorScheme="red" variant="solid">Administrator</Badge>
                  </HStack>
                );
              })
            ) : (
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">No institution administrators assigned</Text>
                  <Text fontSize="sm">
                    Assign at least one user with the administrator institution role so institution-level admin tools behave correctly.
                  </Text>
                </Box>
              </Alert>
            )}

            <HStack justify="flex-end">
              <Button
                colorScheme="blue"
                variant="outline"
                onClick={() => window.location.assign(`/admin/institution-roles?institution=${encodeURIComponent(decodedInstitution)}`)}
              >
                Manage Institution Roles
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Subscription & Scenario Access Management */}
      <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
        <CardHeader>
          <Heading size="md" color="white">Subscription & Scenario Access Management</Heading>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Control scenario generator access and seat management for this institution
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Subscription Tier */}
            <FormControl>
              <FormLabel color="white">Subscription Tier</FormLabel>
              <Select
                value={institutionConfig?.subscriptionTier || formData.subscriptionTier || "individual"}
                onChange={async (e) => {
                  try {
                    await updateConfig({
                      institution: decodedInstitution,
                      configurationUpdates: { subscriptionTier: e.target.value },
                      reason: 'Updated subscription tier via dropdown'
                    });
                    await refetchInstitutionConfig();
                    toast({
                      title: 'Subscription Tier Updated',
                      description: `Changed to ${e.target.value}`,
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  } catch (error) {
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to update subscription tier',
                      status: 'error',
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                }}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                isDisabled={updating}
              >
                <option value="individual">Individual (Personal Institution)</option>
                <option value="institutional">Institutional (Multi-User)</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Individual: Single user with personal institution | Institutional: Multiple users with seat management
              </Text>
            </FormControl>

            {/* Institutional Tier: Seat Management */}
            {institutionConfig?.subscriptionTier === 'institutional' && (
              <>
                <Divider borderColor="gray.600" />
                <Heading size="sm" color="white">Seat Management</Heading>

                <SimpleGrid columns={3} spacing={4}>
                  <Box p={4} bg="blue.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Purchased Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">{purchasedSeats}</Text>
                  </Box>
                  <Box p={4} bg="green.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Used Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">{usedSeats}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {seatsAreLoading ? "Syncing enrollment count..." : "Live from active student enrollments"}
                    </Text>
                  </Box>
                  <Box p={4} bg="purple.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Available Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">
                      {availableSeats}
                    </Text>
                  </Box>
                </SimpleGrid>

                <FormControl>
                  <FormLabel color="white">Total Student Enrollment Limit</FormLabel>
                  <NumberInput
                    value={institutionConfig?.purchasedSeats || 0}
                    min={0}
                  >
                    <NumberInputField
                      bg="gray.900"
                      borderColor="gray.600"
                      color="white"
                      onBlur={async (e) => {
                        try {
                          const newValue = parseInt(e.target.value) || 0;
                          await updateConfig({
                            institution: decodedInstitution,
                            configurationUpdates: { purchasedSeats: newValue },
                            reason: 'Updated student enrollment limit'
                          });
                          await refetchInstitutionConfig();
                          toast({
                            title: 'Student Enrollment Limit Updated',
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                          });
                        } catch (error) {
                          toast({
                            title: 'Error',
                            description: error.message || 'Failed to update student enrollment limit',
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }}
                    />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Maximum number of STUDENTS who can be enrolled at this institution. This does NOT affect instructor scenario generator access.
                  </Text>
                </FormControl>

                {/* Warning if over capacity */}
                {usedSeats > purchasedSeats && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold" color="white">Seat capacity exceeded!</Text>
                      <Text fontSize="sm" color="white">
                        You have {usedSeats} students enrolled but only {purchasedSeats} seats available.
                        Please increase the student enrollment limit or remove students.
                      </Text>
                    </Box>
                  </Alert>
                )}
              </>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Additional Settings */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Additional Settings</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Require Preceptor Approval</FormLabel>
                <Switch
                  isChecked={formData.requirePreceptorApproval || false}
                  onChange={(e) => handleUpdateField("requirePreceptorApproval", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Access Code System Enabled</FormLabel>
                <Switch
                  isChecked={formData.accessCodeSystemEnabled || false}
                  onChange={(e) => handleUpdateField("accessCodeSystemEnabled", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>
    </VStack>
  );
}
